/**
 * Onboarding domain types, shared between server and web.
 *
 * Onboarding is the pre-start gathering of a new employee's details
 * (personal info, next of kin, bank details, uniform sizes), their DBS
 * number, and a set of documents to read and sign. It is modelled as its
 * own programme (a mobilisations row with kind='onboarding') so it reuses
 * the event / task / scoping machinery while staying isolated from any
 * induction-day chain.
 *
 * Two storage rules underpin everything here (see CLAUDE.md plan):
 *   - The event chain records that a capture *happened* (proof of who /
 *     when, hash-chained) but NOT the raw values. Signed documents are the
 *     exception: the signature lives on the event because tamper-evidence
 *     is the whole point there.
 *   - The actual field values live in people.metadata.onboarding.* so they
 *     stay queryable and, crucially, redactable for GDPR erasure.
 *
 * Because events never carry the PII, the data-classification boundary
 * (what a mobilisation desk may see) only has to reason about which
 * metadata fields are allowed to cross — see FIELD_CLASSIFICATION below.
 */

/** A programme is either an induction day or the durable onboarding home. */
export const PROGRAMME_KINDS = ['mobilisation', 'onboarding'] as const;
export type ProgrammeKind = (typeof PROGRAMME_KINDS)[number];

/**
 * The kinds of onboarding task. The kind drives which form / UI renders on
 * the inductee's page and how the submission is validated server-side.
 * Each is realised as a normal task on the onboarding chain (TaskAssigned
 * etc) with onboarding_kind set, so the existing task lifecycle applies.
 */
export const ONBOARDING_TASK_KINDS = [
  'personal_details',
  'next_of_kin',
  'bank_details',
  'uniform_sizes',
  'dbs_number',
  'document_ack',
  // A configurable HR-authored form (see onboardingForm.ts). The five legacy
  // capture kinds above are removed in phase 6, leaving 'form' + 'document_ack'.
  'form',
  // A plain file upload (scan/photo of a document, e.g. P45). The inductee
  // attaches one or more files; HR approves having seen them.
  'upload',
  // A simple to-do the inductee marks done (e.g. "bring your passport").
  'todo',
  // A staged screening check (DBS / DVLA). The inductee applies via an
  // external portal (instructions = the task description) and ticks
  // "I have applied"; HR marks the application processed, then verifies
  // the physical certificate on day one. Must carry a preflight_key,
  // which identifies the check type and gates the pre-employment row.
  'check',
  // An e-learning module (see trainingModule.ts): slides + quiz with a pass
  // mark. Auto-completes on a passing attempt (no HR approval step).
  'training',
] as const;
export type OnboardingTaskKind = (typeof ONBOARDING_TASK_KINDS)[number];

/** Kinds of ad-hoc catalog task (the others are content: forms / documents). */
export const CATALOG_TASK_KINDS = ['upload', 'todo', 'check'] as const;
export type CatalogTaskKind = (typeof CATALOG_TASK_KINDS)[number];

/* -------------------------------------------------------------------------
 * Screening-check workflow stages (kind='check' tasks)
 * -----------------------------------------------------------------------*/

/**
 * The journey of a screening check, projected from the task's events:
 *   not_applied        — assigned, inductee hasn't applied yet
 *   applied            — inductee ticked "I have applied" (TaskSubmitted);
 *                        HR needs to confirm it on the external portal
 *   processed          — HR marked the application processed
 *                        (CheckProcessed); the inductee is waiting for the
 *                        paper certificate and must bring the original in
 *   verified           — HR physically inspected the certificate
 *                        (TaskApproved); terminal, flips the gate to 'ok'
 *   changes_requested  — HR sent it back (TaskRejected) with a reason
 */
export const CHECK_STAGES = [
  'not_applied',
  'applied',
  'processed',
  'verified',
  'changes_requested',
] as const;
export type CheckStage = (typeof CHECK_STAGES)[number];

/** Human label for a check stage (UI, both /me and the HR checks list). */
export const CHECK_STAGE_LABEL: Record<CheckStage, string> = {
  not_applied: 'Pending application',
  applied: 'Pending HR Review',
  processed: 'Awaiting Physical Check',
  verified: 'Complete',
  changes_requested: 'Action needed',
};

/* -------------------------------------------------------------------------
 * Pre-employment checklist gates (HR compliance) <-> onboarding task linkage
 *
 * Known to users as the "Pre-Employment Checklist". The internal identifiers
 * (preflight, p45, dvla, the metadata field names) predate the rename and
 * stay as-is: changing them would mean migrating people.metadata and the
 * preflight_key columns for no user-visible gain.
 * -----------------------------------------------------------------------*/

/**
 * The HR pre-employment gate checks a task can satisfy. Each maps to a field
 * on people.metadata.preflight. A task tagged with one of these flips that
 * gate to 'ok' (and locks it in the UI) when HR approves the task.
 */
export const PREFLIGHT_KEYS = ['dbs', 'right_to_work', 'dvla', 'p45'] as const;
export type PreflightKey = (typeof PREFLIGHT_KEYS)[number];

/** Gate key -> the people.metadata.preflight field it controls. */
export const PREFLIGHT_FIELD: Record<
  PreflightKey,
  'dbs_status' | 'right_to_work' | 'dvla_check' | 'p45_received'
> = {
  dbs: 'dbs_status',
  right_to_work: 'right_to_work',
  dvla: 'dvla_check',
  p45: 'p45_received',
};

/** Human label for a gate key (UI). */
export const PREFLIGHT_LABEL: Record<PreflightKey, string> = {
  dbs: 'DBS',
  right_to_work: 'Right to work',
  dvla: 'DVLA check',
  p45: 'P45 or HMRC Starter Checklist',
};

/**
 * A template is a per-role bundle of references to existing content. Applying
 * it assigns each referenced document / form / task. Order is preserved.
 */
export const TEMPLATE_ITEM_TYPES = ['document', 'form', 'task', 'training_module'] as const;
export type TemplateItemType = (typeof TEMPLATE_ITEM_TYPES)[number];

export interface OnboardingTemplateItem {
  type: TemplateItemType;
  /** id of the onboarding_documents / onboarding_forms / onboarding_tasks / training_modules row. */
  id: string;
}

export interface OnboardingTemplate {
  id: string;
  /** Denormalised display mirror of the target's name (role or category). */
  role: string;
  /** Exactly one of role_id / category_id is set: the template is either
   * specific to one role, or the shared baseline for a role category. */
  role_id: string | null;
  category_id: string | null;
  items: readonly OnboardingTemplateItem[];
  created_at: string; // ISO 8601
}

/** Shape used by the create / update endpoints. Exactly one of role_id /
 * category_id; the server resolves it to the display name. */
export interface OnboardingTemplateInput {
  role_id?: string | null;
  category_id?: string | null;
  items: readonly OnboardingTemplateItem[];
}

/** A selectable choice inside a document the inductee must pick before signing. */
export interface OnboardingDocumentOption {
  value: string;
  label: string;
}

/**
 * A document the inductee reads and signs. The document itself is
 * versioned: replacing it inserts a new version rather than mutating the
 * old one, so a signature always resolves to the exact bytes signed.
 */
export interface OnboardingDocument {
  id: string;
  /** Stable slug, e.g. 'code-of-conduct'. */
  key: string;
  /** When true, signing puts it into awaiting-review until HR approves. */
  requires_approval: boolean;
  /** When set, HR approval of this document satisfies the matching gate. */
  preflight_key: PreflightKey | null;
  created_at: string; // ISO 8601
  archived_at: string | null;
}

export interface OnboardingDocumentVersion {
  id: string;
  document_id: string;
  /** Monotonic per document, starting at 1. */
  version: number;
  title: string;
  /** Storage key for the source PDF. */
  pdf_s3_key: string;
  /** Choice set the inductee must pick from before signing, when required. */
  options: readonly OnboardingDocumentOption[];
  requires_option: boolean;
  /**
   * When true, publishing this version re-opens the acknowledgement task
   * for everyone who signed an earlier version (forced re-sign). Default
   * off: a new version normally only affects new starters.
   */
  requires_resign: boolean;
  published_at: string | null;
  created_at: string; // ISO 8601
}

/* -------------------------------------------------------------------------
 * Field-level data classification (the leak boundary)
 * -----------------------------------------------------------------------*/

/**
 * Where a captured onboarding field is allowed to surface.
 *   - 'self'        the inductee's own /me view
 *   - 'onboarding'  the onboarding / HR dashboard and HR readers
 *   - 'ppe_desk'    the PPE station on an induction day (sizes only)
 *   - 'all_desks'   every mobilisation station (e.g. a preferred name)
 *
 * Anything NOT listed for a surface must never be sent to a reader on that
 * surface. Mobilisation desks (reception, station_*, coordinator without
 * the onboarding role) only ever see 'ppe_desk' / 'all_desks'-tagged
 * fields, plus the contents-free task checklist.
 */
export const FIELD_SURFACES = ['self', 'onboarding', 'ppe_desk', 'all_desks'] as const;
export type FieldSurface = (typeof FIELD_SURFACES)[number];

/**
 * The single source of truth for which metadata.onboarding.* fields may
 * cross to which surface. Uniform sizes are the ONLY sensitive-category
 * field that reaches the mobilisation side (the PPE desk pre-fill); bank
 * details, next of kin, home address and the DBS number are onboarding /
 * self only and must never leak to a station.
 */
export const FIELD_CLASSIFICATION: Record<string, readonly FieldSurface[]> = {
  personal_details: ['self', 'onboarding'],
  next_of_kin: ['self', 'onboarding'],
  bank_details: ['self', 'onboarding'],
  home_address: ['self', 'onboarding'],
  dbs_number: ['self', 'onboarding'],
  uniform_sizes: ['self', 'onboarding', 'ppe_desk'],
} as const;

/**
 * True if the given onboarding field may be revealed on the given surface.
 * Unknown fields default to closed (no surface) so adding a new field
 * without classifying it fails safe rather than leaking.
 */
export function fieldVisibleOn(field: string, surface: FieldSurface): boolean {
  return FIELD_CLASSIFICATION[field]?.includes(surface) ?? false;
}
